import './services.scss';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStores } from 'src/components/store';
import { convertServiceAndStatusToCustomServiceType, getServiceInactiveDirection } from 'src/utils/versionUtils';
import { Toggle } from 'src/components/shared/toggle/toggle';
import {
    BulkProtectionOptions,
    Direction,
    DisableReasons,
    EditorMode,
    MainPages,
    ProtectionMenuNameOptions,
    ServiceActionType,
    ServicesFilterNames
} from 'src/utils/enums';
import { ServiceAndStatus } from 'src/dtos/service-with-status.dto';
import { HeaderColumnMax } from '../protection/header-column-max/header-column-max';
import { ServiceItemTooltip } from './service-item-tooltip/service-item-tooltip';
import Loader from '../../shared/loader/loader';
import { Filters } from '../../shared/filters/filters';
import { servicesFiltersConfig } from '../../shared/filters/filter-config/services-filters';
import { BulkPolicyUpdate } from '../../shared/bulk-options/bulk-policy-update';
import { NoResult } from '../../shared/no-result/no-result';
import { Service } from '@src/dtos/version.dto';
import _ from 'lodash';
import { useServicesCrudConfig } from '../inventory/services/services-crud-config';
import { ServicesUpdateDto } from '@src/dtos/default-service-update.dto';
import { useActivation } from '@src/utils/activationUtil';
import CustomButton from '../../shared/custom-button/custom-button';
import { createServiceColumns } from '../inventory/services/services-column-config';
import CustomIcon, { IconType } from '../../shared/custom-icon/custom-icon';
import { DataEditorModal } from '../../shared/edit-row-modal/edit-row-modal';
import { TooltipDisableReason } from '../../shared/tooltip-disable-message/tooltip-disable-reason';
import { SystemConfiguration } from '@src/utils/system-configuration';
import { getCurrentFiltersCombinationLabel } from '@src/utils/filters-util';
import { ServiceAction } from './service-item-action/service-action';
import { isTCPOrUDPAllService } from '@src/utils/direction-utils';import { usePageViewEvent } from '../../shared/hooks/use-page-view-event';

export const ServicesPolicy: React.FC = observer(() => {
    const { fwVersionStore, ruleStore } = useStores();
    const { defaultServiceDtoList } = SystemConfiguration.getInstance();
    const [services, setServices] = useState<ServiceAndStatus[] | undefined>(undefined);
    const config = useServicesCrudConfig();
    const { AllowInternetToMyNetworkNonWebServices, AllowMyNetworkToInternetNonWebServices,
        MyNetworkToInternetServices, MyNetworkToInternetAllowedApplications,
        InternetToMyNetworkAllowedServices, InternetToMyNetworkAllowedApplications
    } = ruleStore;

    const serviceColumns = useMemo(() => createServiceColumns(ruleStore.specialServices), [ruleStore.specialServices]);
    const { rulesByPage } = useActivation();
    const [data, setData] = useState<Service[]>([]);
    const [isEditorModalOpen, setIsEditorModalOpen] = useState(false);

    usePageViewEvent(MainPages.POLICY, ProtectionMenuNameOptions.SERVICES);

    useEffect(() => {
        fwVersionStore.setProtectionType(ProtectionMenuNameOptions.SERVICES);
    }, []);

    const originalServices: ServiceAndStatus[] = useMemo(() => {
        if (!defaultServiceDtoList?.length) {
            return [];
        }
        else {
            const defaultServiceList = ruleStore.defaultServicesWithStatus;
            const customServiceList = ruleStore.customServicesWithStatus;
            const serviceList = _.concat(defaultServiceList, customServiceList);

            const pageRules = rulesByPage[BulkProtectionOptions.SERVICES];
            if (pageRules.some(_ => !_)) {
                return [];
            }
            const fromInbound = [
                MyNetworkToInternetServices,
                MyNetworkToInternetAllowedApplications,
                AllowMyNetworkToInternetNonWebServices
            ];
            const fromOutbound = [
                AllowInternetToMyNetworkNonWebServices,
                InternetToMyNetworkAllowedServices,
                InternetToMyNetworkAllowedApplications
            ];
            const isAllRuleDisable = pageRules.every(rule => rule?.disabled);
            const isAllRuleActive = pageRules.every(rule => !!rule && !rule.disabled);
            const data = getServiceInactiveDirection(pageRules, fromInbound, fromOutbound, serviceList, isAllRuleDisable, isAllRuleActive);
            return [...data];
        }
    }, [fwVersionStore.selectedFwVersion?.content]);

    useEffect(() => {
        const defaultServiceList = ruleStore.defaultServicesWithStatus;
        const defaultServices = defaultServiceList.map(
            _ => convertServiceAndStatusToCustomServiceType(_));

        const customService = ruleStore.customServices;
        setData([...customService, ...defaultServices]);
    }, [ruleStore.customServices, ruleStore.defaultServicesWithStatus]);

    const newRowData = useMemo(() => {
        return config?.newRecord as Service;
    }, [config]);

    const onChange = async (serviceDisplayName: string, isAllow: boolean, direction: Direction) => {
        await fwVersionStore.setIsDirty();
        const originalData = _.cloneDeep(services) || [];
        const servicesCopy = _.cloneDeep(services) || [];
        const index = (servicesCopy || []).findIndex(_ => _.name === serviceDisplayName);
        const serviceState = isAllow ? ServiceActionType.ALLOW : ServiceActionType.BLOCK;
        servicesCopy[index] = {
            ...servicesCopy[index],
            inboundState: direction === Direction.Inbound ? serviceState : servicesCopy[index].inboundState,
            outboundState: direction === Direction.Outbound ? serviceState : servicesCopy[index].outboundState
        };
        setServices(servicesCopy);

        const appServicesUpdate: ServicesUpdateDto = {
            serviceDisplayName,
            inboundState: servicesCopy[index].inboundState,
            outboundState: servicesCopy[index].outboundState
        };

        try {
            await fwVersionStore.updateDefaultServiceStatus(appServicesUpdate);
        }
        catch (e) {
            setServices(originalData);
        }
    };

    const save = async (editedRowData: Service) => {
        await config?.saveCb?.(editedRowData);
    };

    const actionFilterOptionLabel = getCurrentFiltersCombinationLabel(servicesFiltersConfig.localStorageKey || '', servicesFiltersConfig.dropdownList || [], 0);
    const originFilterOptionLabel = getCurrentFiltersCombinationLabel(servicesFiltersConfig.localStorageKey || '', servicesFiltersConfig.dropdownList || [], 1);
    const noResultsTitle = useMemo(() => {
        return actionFilterOptionLabel === ServicesFilterNames.All && originFilterOptionLabel === ServicesFilterNames.All ?
            'No Search Result' : `No ${actionFilterOptionLabel !== ServicesFilterNames.All ? actionFilterOptionLabel : ''} 
                ${originFilterOptionLabel !== ServicesFilterNames.All ? originFilterOptionLabel : ''} Services`;
    }, [actionFilterOptionLabel, originFilterOptionLabel]);

    const disableReason = useCallback((serviceName: string, serviceState: ServiceActionType) =>
        serviceState === ServiceActionType.ALLOW && isTCPOrUDPAllService(serviceName, ruleStore.specialServices) && !fwVersionStore.getDisableReason
            ? DisableReasons.TCP_All_OR_UDP_All : fwVersionStore.getDisableReason, [fwVersionStore.getDisableReason, ruleStore.specialServices]);

    const setDataFromFilter = (data: ServiceAndStatus[]) => {
        if (fwVersionStore.selectedFwVersion) {
            setServices(data);
        }
    };

    const getProperDisplay = (data: ServiceAndStatus[] | undefined) => {
        if (!data) return null;
        if (data.length === 0) return <NoResult title={noResultsTitle} />;
        return <>
            <HeaderColumnMax/>
            <div className="services-item-content" data-testid={'services-group'}>
            {data?.sort((a, b) => a.name.localeCompare(b.name))?.map(service =>
                <div key={service.id} className="services-item card">
                    <div className="titles">
                        <div className={`service-name`}>{service.name}</div>
                    </div>
                    <div className={'buttons'}>
                        <span className="button-wrapper black icon">
                            <ServiceItemTooltip title={service.serviceTitle}
                                descriptionList={service.serviceDescription} /></span>
                        {config.disableEditReason === undefined &&
                            <ServiceAction item={service}
                                crudConfig={config}
                                isCustom={service.isCustom} />}
                    </div>
                    <div className={`outbound-inactive-${false} inbound-inactive-${false} toggle-buttons`}>
                        {service.inboundState !== ServiceActionType.NOT_APPLY &&
                            <Toggle value={service.inboundState === ServiceActionType.ALLOW}
                                anotherClassName={'inbound-toggle'}
                                disableReason={disableReason(service.name, service.inboundState)}
                                onChange={(isAllow: boolean) => onChange(service.name, isAllow, Direction.Inbound)}
                                withIcon={true}
                                inactiveStateIcon={false} title={'Service Inactive'} />}

                        {service.outboundState !== ServiceActionType.NOT_APPLY &&
                            <Toggle value={service.outboundState === ServiceActionType.ALLOW}
                                anotherClassName={'outbound-toggle'}
                                disableReason={disableReason(service.name, service.outboundState)}
                                onChange={(isAllow: boolean) => onChange(service.name, isAllow, Direction.Outbound)}
                                withIcon={true}
                                inactiveStateIcon={false} title={'Service Inactive'} />}
                    </div>
                </div>)}
        </div></>;
    };

    return <div className="services-policy page-content" data-testid="services">
        <div className="header-filter-wrapper">
            <Filters filterConfig={servicesFiltersConfig}
                setData={setDataFromFilter}
                originalData={originalServices || []}
            />
            <TooltipDisableReason overlayClassName="row-limit-tooltip" disableReason={config.disableEditReason}>
                <CustomButton prefixIcon={<CustomIcon type={IconType.PLUS} />}
                    isDisabled={config.disableEditReason !== undefined}
                    className={'secondary-btn add-service-btn'}
                    label={'Add'}
                    onClick={() => setIsEditorModalOpen(true)}></CustomButton>
            </TooltipDisableReason>
            <BulkPolicyUpdate tab={BulkProtectionOptions.SERVICES} />
        </div>

        {getProperDisplay(services)}
        {(!data || fwVersionStore.isLoading || !fwVersionStore.selectedFwVersion) ? <Loader type="full-screen" /> : <></>}

        <DataEditorModal isOpen={isEditorModalOpen}
            closeModal={() => setIsEditorModalOpen(false)}
            columnsConfig={serviceColumns}
            onSave={save}
            header={config.createNewText}
            okBtnText={'Add Service'}
            data={data}
            mode={EditorMode.NEW}
            rowData={newRowData}
            crudConfig={config} />
    </div>;
});

